import React, { useCallback } from 'react';
import { Box, Portal, useToast, useDisclosure } from '@chakra-ui/react';
import { Line } from 'rc-progress';
import { useDropzone } from 'react-dropzone';
import DropzonePlaceholder from '../DropzonePlaceholder';
import { UploadPopoverProps } from './UploadPopover.types';

const UploadPopover: React.FC<UploadPopoverProps> = ({
  withPortal,
  onDrop,
  isUploading,
  progress,
  disabled,
  maxPictures,
  urls,
  withLimit,
}) => {
  const { onOpen, isOpen, onClose } = useDisclosure();

  const handleDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      onClose();
      onDrop(acceptedFiles);
    },
    [onDrop, onClose],
  );

  const toast = useToast();
  const remaining = Math.max((maxPictures ?? 20) - (urls?.length ?? 0), 0);

  const inputDisabled = withLimit ? disabled || remaining === 0 : disabled;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    maxFiles: withLimit ? remaining : 20,
    accept: 'image/*',
    onDropRejected: (fileRejections) => {
      const tooManyFiles = fileRejections.some((rej) =>
        rej.errors.some((err) => err.code === 'too-many-files'),
      );
      if (tooManyFiles) {
        toast({
          title: 'Limite de imagens atingido',
          description: `Você só pode selecionar até ${
            maxPictures ?? 20
          } imagens.`,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
  });

  const renderInPortal = useCallback(
    (content: JSX.Element) => {
      if (withPortal) {
        return <Portal appendToParentPortal>{content}</Portal>;
      }
      return content;
    },
    [withPortal],
  );

  return (
    <Box>
      <Box
        maxWidth="300px"
        maxHeight="180px"
        display="flex"
        bg="gray.100"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor="gray.300"
        borderRadius="10px"
        overflow="hidden"
        {...getRootProps()}
        style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
      >
        <input {...getInputProps()} disabled={inputDisabled} />
        <DropzonePlaceholder />
      </Box>
      {isUploading && (
        <div style={{ paddingTop: 10 }}>
          <Line
            percent={progress}
            strokeWidth={4}
            strokeColor="#0794B2"
            trailColor="#EBEBEB"
          />
        </div>
      )}
    </Box>
  );
};

export default UploadPopover;
